<template>
  <div :class="bem.b()" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
    <!-- 触发元素 -->
    <div :class="bem.e('trigger')" ref="triggerRef">
      <slot></slot>
    </div>

    <!-- 提示内容 -->
    <!-- <Teleport to="body"> -->
      <transition
        :name="transition"
        @before-enter="onBeforeEnter"
        @after-enter="onShow"
        @before-leave="onBeforeHide"
        @after-leave="onHide"
      >
        <div
          v-show="tooltipVisible"
          :class="[bem.e('popper'), bem.m(effect), tooltipClass]"
          :style="popperStyle"
          ref="popperRef"
        >
          <div :class="bem.e('content')">
            <slot name="content">{{ content }}</slot>
          </div>
          <div v-if="showArrow" :class="bem.e('arrow')" data-popper-arrow></div>
        </div>
      </transition>
    <!-- </Teleport> -->
  </div>
</template>

<script lang="ts">
// 先定义组件选项
export default {
  name: 'cms-tooltip'
}
</script>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { createNamespace } from '@chenms-zj/utils/create'
import { tooltipEmits, tooltipProps } from './tooltip'
import { createPopper } from '@popperjs/core'
import type { Instance as PopperInstance } from '@popperjs/core'

const props = defineProps(tooltipProps)
const emit = defineEmits(tooltipEmits)
const bem = createNamespace('tooltip')

// 引用元素
const triggerRef = ref<HTMLElement | null>(null)
const popperRef = ref<HTMLElement | null>(null)

// popper实例
let popperInstance: PopperInstance | null = null

// 控制提示框显示状态
const tooltipVisible = ref(false)

// 控制延迟计时器
let showTimer: ReturnType<typeof setTimeout> | null = null
let hideTimer: ReturnType<typeof setTimeout> | null = null

// 计算popper样式
const popperStyle = computed(() => {
  return {
    zIndex: '9999'
  }
})

// 监听modelValue变化
watch(
  () => props.modelValue,
  val => {
    if (val === undefined) return
    tooltipVisible.value = val
  }
)

// 初始化popper
const initPopper = () => {
  if (!triggerRef.value || !popperRef.value) return

  if (popperInstance) {
    popperInstance.destroy()
    popperInstance = null
  }

  popperInstance = createPopper(triggerRef.value, popperRef.value, {
    placement: props.placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 10]
        }
      },
      {
        name: 'arrow',
        options: {
          padding: 5
        }
      }
    ]
  })
}

// 显示tooltip
const show = () => {
  if (props.disabled || (props.manual && props.modelValue === undefined)) return

  clearTimeout(hideTimer!)
  hideTimer = null

  if (props.showDelay) {
    showTimer = setTimeout(() => {
      tooltipVisible.value = true
      emit('update:modelValue', true)
    }, props.showDelay)
  } else {
    tooltipVisible.value = true
    emit('update:modelValue', true)
  }
}

// 隐藏tooltip
const hide = () => {
  if (props.disabled || (props.manual && props.modelValue === undefined)) return

  clearTimeout(showTimer!)
  showTimer = null

  if (props.hideDelay) {
    hideTimer = setTimeout(() => {
      tooltipVisible.value = false
      emit('update:modelValue', false)
    }, props.hideDelay)
  } else {
    tooltipVisible.value = false
    emit('update:modelValue', false)
  }
}

// 鼠标进入触发元素
const onMouseEnter = () => {
  if (props.trigger === 'hover') {
    show()
  }
}

// 鼠标离开触发元素
const onMouseLeave = () => {
  if (props.trigger === 'hover') {
    hide()
  }
}

// 点击事件处理
const onClick = () => {
  if (props.trigger === 'click') {
    if (tooltipVisible.value) {
      hide()
    } else {
      show()
    }
  }
}

// 聚焦事件处理
const onFocus = () => {
  if (props.trigger === 'focus') {
    show()
  }
}

// 失焦事件处理
const onBlur = () => {
  if (props.trigger === 'focus') {
    hide()
  }
}

// 生命周期钩子
const onBeforeEnter = () => {
  emit('before-show')
  nextTick(() => {
    initPopper()
  })
}

const onShow = () => {
  emit('show')
  if (popperInstance) {
    popperInstance.update()
  }
}

const onBeforeHide = () => {
  emit('before-hide')
}

const onHide = () => {
  emit('hide')
}

// 添加事件监听
onMounted(() => {
  if (props.trigger === 'click') {
    triggerRef.value?.addEventListener('click', onClick)
  } else if (props.trigger === 'focus') {
    triggerRef.value?.addEventListener('focus', onFocus)
    triggerRef.value?.addEventListener('blur', onBlur)
  }

  // 初始状态
  if (props.modelValue !== undefined) {
    tooltipVisible.value = props.modelValue
  }
})

// 清理事件监听和实例
onBeforeUnmount(() => {
  if (props.trigger === 'click') {
    triggerRef.value?.removeEventListener('click', onClick)
  } else if (props.trigger === 'focus') {
    triggerRef.value?.removeEventListener('focus', onFocus)
    triggerRef.value?.removeEventListener('blur', onBlur)
  }

  if (popperInstance) {
    popperInstance.destroy()
    popperInstance = null
  }

  clearTimeout(showTimer!)
  clearTimeout(hideTimer!)
})
</script>

<style scoped lang="scss">
.cms-tooltip {
  display: inline-flex;
  position: relative;

  &__trigger {
    display: inline-flex;
  }

  &__popper {
    position: absolute;
    border-radius: 4px;
    padding: 10px;
    z-index: 2000;
    font-size: 12px;
    line-height: 1.2;
    min-width: 10px;
    word-wrap: break-word;
    visibility: visible;

    &.cms-tooltip--dark {
      background: #303133;
      color: #fff;
    }

    &.cms-tooltip--light {
      background: #fff;
      border: 1px solid #e4e7ed;
      color: #606266;
    }
  }

  &__content {
    text-align: center;
  }

  &__arrow {
    position: absolute;
    width: 10px;
    height: 10px;
    z-index: -1;

    &::before {
      position: absolute;
      width: 10px;
      height: 10px;
      z-index: -1;
      content: ' ';
      transform: rotate(45deg);
      background: #303133;
    }

    .cms-tooltip--light &::before {
      background: #fff;
      border: 1px solid #e4e7ed;
    }
  }
}

// 箭头位置样式
[data-popper-placement^='top'] .cms-tooltip__arrow {
  bottom: -5px;
}

[data-popper-placement^='bottom'] .cms-tooltip__arrow {
  top: -5px;
}

[data-popper-placement^='left'] .cms-tooltip__arrow {
  right: -5px;
}

[data-popper-placement^='right'] .cms-tooltip__arrow {
  left: -5px;
}

// 过渡动画
.cms-tooltip-fade-enter-active,
.cms-tooltip-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.cms-tooltip-fade-enter-from,
.cms-tooltip-fade-leave-to {
  opacity: 0;
  // transform: translateY(-5px);
}
</style>
